import React, {
  ChangeEvent,
  forwardRef,
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'
import {Badge} from '../Badge'
import {FS_PRIVACY_MASK_CLASS, TextTransform} from '../helpers/InputBase'
import {Icon} from '../Icon'
import {
  CaptionStyles,
  FieldStyles,
  FieldWrapperStyles,
  InputLabel,
  InputTextStyles
} from '../InputText/styles'

interface Props {
  /** Field autocomplete id */
  autoComplete?: string
  /** Message to display below input. Changes color along with validation highlights. */
  caption?: ReactNode
  /** The options of the select element */
  children: ReactNode
  /** An object of custom props that is not explicitly supported, but might be required for some custom implementations.
   * This can also be used to override the explicit props, like a custom onChange function.
   * or as a way to get rid of the chrome autofill.
   * */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customProps?: Record<string, any>
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Whether the input is disabled */
  isDisabled?: boolean
  /** ID of this component, passed down by the controlling parent component */
  id?: string
  /** Whether the input content is private and should be masked in FullStory */
  isPrivate?: boolean
  /** Add an asterisk to the label */
  isRequired?: boolean
  /** Name of this component, passed down by the controlling parent component */
  name?: string
  /** An onFocus callback passed down by the controlling parent component */
  onFocus?: React.FocusEventHandler<HTMLSelectElement>
  /** An onBlur callback passed down by the controlling parent component */
  onBlur?: React.FocusEventHandler<HTMLSelectElement>
  /** An onChange callback passed down by the controlling parent component */
  onChange?: (value: string) => void
  /** Callback that fires when the input is clicked */
  onClick?: () => void
  /** Content to be used as the input label and placeholder. It must be translated string */
  label?: string
  /** The size of the input */
  size?: 'sm' | 'lg'
  /** CSS text-transform style to apply to the input field */
  textTransformType?: TextTransform
  /** The value passed down by the controlling parent component */
  value?: string
  /** Optional aria-label for screen-readers, used when no label is present */
  ariaLabel?: string
  /** Put the input in the valid state */
  /** Whether the input shows or not the validation badge */
  withValidationBadge?: boolean
  isValid?: boolean
  /** Put the input in the invalid state */
  isInvalid?: boolean
  /** className prop is used by Linaria to pass CSS variables in cases when we need to style custom components in this way: `const StyledInputSelect = styled(InputSelect)` */
  className?: string
  /** Any component to display before the input */
  prefix?: ReactNode
}

const Field = styled.label<{prefixWidth: number}>`
  pointer-events: none;
  position: relative;
  &:focus-within,
  &.--is-filled {
    /* Dynamically position the label when the prefix is present */
    &.--has-prefix .label {
      transform: ${({prefixWidth}) =>
        `translateX(${`-${prefixWidth + 10}px`}) translateY(-18px)`};
    }
  }
  &.--size-sm {
    height: 32px;
    min-height: 32px;
    padding: 0 ${cssTheme.layout.spacing.s300};
    select {
      ${cssTheme.typography.bodyS};
      color: ${cssTheme.colors.input.default.label.empty};
      &.--is-filled {
        color: ${cssTheme.colors.input.default.text};
      }
      &:disabled {
        color: ${cssTheme.colors.input.disabled.text};
      }
    }
  }
  .label {
    max-width: 100%;
    display: inline-block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const InputGroupStyles = styled.div`
  height: 26px;
  width: 100%;
  position: relative;
`

const InputSelectElement = styled.select<{
  textTransformType?: TextTransform
  value?: Props['value']
  prefixWidth: number
  isSmallSize: boolean
}>`
  position: absolute;
  pointer-events: auto;
  background-color: transparent;
  border-radius: ${cssTheme.layout.radius.md};
  left: 0;
  bottom: 0;
  right: 0;
  top: 0;
  /* "FieldStyles" overrides padding so we have to use !important here for now */
  padding-left: ${({prefixWidth, isSmallSize}) => {
    const gap = prefixWidth > 0 ? cssTheme.layout.spacing.s250 : '0px'
    const padding = isSmallSize
      ? cssTheme.layout.spacing.s300
      : cssTheme.layout.spacing.s400
    return `calc(${gap} + ${padding} + ${prefixWidth}px)`
  }} !important;
  padding-right: calc(${cssTheme.layout.spacing.s250} + 20px) !important;
  appearance: none;
  -webkit-appearance: none;
  z-index: 0;
  text-transform: ${({textTransformType}) =>
    textTransformType ? textTransformType : TextTransform.none};
  &:disabled {
    opacity: inherit;
  }
`

const SuffixWrapper = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  gap: ${cssTheme.layout.spacing.s250};
  align-items: center;
`

const CaratWrapper = styled.div<{
  isOpen: boolean
  disabled?: boolean
}>`
  width: 20px;
  height: 20px;
  transition: transform 0.2s ease 0s;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${({isOpen}) => (isOpen ? 'rotateZ(0)' : 'rotateZ(180deg)')};
  color: ${({disabled}) =>
    disabled
      ? cssTheme.colors.input.disabled.icon
      : cssTheme.colors.input.default.icon};
`

const PrefixWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 10;
  color: ${cssTheme.colors.input.default.icon};
`

export const InputSelect = forwardRef<HTMLSelectElement, Props>(
  (
    {
      autoComplete,
      caption,
      children,
      dataId,
      isDisabled,
      id,
      isInvalid,
      isValid,
      isPrivate,
      name,
      onBlur,
      onChange,
      onClick,
      onFocus,
      label,
      size = 'lg',
      textTransformType = TextTransform.none,
      value,
      className,
      customProps,
      ariaLabel,
      prefix,
      isRequired,
      withValidationBadge
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [isFilled, setIsFilled] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const [prefixWidth, setPrefixWidth] = useState(0)
    const prefixRef = useRef<HTMLDivElement>(null)

    const handleBlur = (event: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(false)
      setIsOpen(false)
      if (onBlur) {
        onBlur(event)
      }
    }

    const handleChange = (event: ChangeEvent) => {
      const newValue = (event.currentTarget as HTMLSelectElement).value
      setIsOpen(false)
      if (onChange) {
        onChange(newValue)
      }
      setIsFilled(Boolean(newValue))
    }

    const handleFocus = (event: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(true)
      onFocus?.(event)
    }
    const handleClick = () => {
      if (onClick) onClick()
      setIsOpen(true)
    }

    useEffect(() => {
      setIsFilled(Boolean(value?.length))
    }, [value])

    useEffect(() => {
      if (prefix && prefixRef.current) {
        setPrefixWidth(prefixRef.current.offsetWidth)
      }
    }, [prefix, prefixRef?.current?.offsetHeight])

    const isSmallSize = size === 'sm'
    return (
      <div css={FieldWrapperStyles} className={className}>
        <Field
          htmlFor={id}
          prefixWidth={prefixWidth}
          css={FieldStyles}
          className={cx(
            isInvalid && '--is-invalid',
            isFilled && '--is-filled',
            isDisabled && '--is-disabled',
            !!prefix && '--has-prefix',
            isSmallSize && '--size-sm'
          )}
        >
          <InputSelectElement
            autoComplete={autoComplete}
            data-field-error={isInvalid}
            data-id={dataId}
            disabled={isDisabled}
            id={id}
            name={name}
            value={value}
            textTransformType={textTransformType}
            onBlur={handleBlur}
            onChange={handleChange}
            onClick={handleClick}
            onFocus={handleFocus}
            aria-label={ariaLabel}
            {...customProps}
            css={InputTextStyles}
            className={cx(
              isPrivate && FS_PRIVACY_MASK_CLASS,
              isFilled && '--is-filled'
            )}
            prefixWidth={prefixWidth}
            ref={ref}
            isSmallSize={isSmallSize}
          >
            {Boolean(label) && (
              <option disabled hidden value="">
                {isFocused || isSmallSize ? label : ''}
              </option>
            )}
            {children}
          </InputSelectElement>
          {prefix && <PrefixWrapper ref={prefixRef}>{prefix}</PrefixWrapper>}
          <InputGroupStyles>
            {label && !isSmallSize && (
              <InputLabel className="label">
                {label}
                {isRequired && <span className="asterisk">*</span>}
              </InputLabel>
            )}
          </InputGroupStyles>

          <SuffixWrapper>
            {withValidationBadge && (
              <>
                {isInvalid && (
                  <Badge iconName="WarningMark" size="xs" variant="danger" />
                )}
                {isValid && (
                  <Badge iconName="Checkmark" size="xs" variant="success" />
                )}
              </>
            )}
            <CaratWrapper isOpen={isOpen} disabled={isDisabled}>
              <Icon name="CaratUp" size="xs" />
            </CaratWrapper>
          </SuffixWrapper>
        </Field>
        {caption && (
          <div
            css={CaptionStyles}
            className={cx(
              isInvalid && '--is-invalid',
              isDisabled && '--is-disabled'
            )}
          >
            {caption}
          </div>
        )}
      </div>
    )
  }
)

InputSelect.displayName = 'InputSelect'
